import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, EnvelopeSimple, LockKey, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { api, errMsg } from "../lib/api";

const Shell = ({ title, sub, children }) => (
  <div className="relative grid min-h-screen place-items-center overflow-hidden bg-[#05050a] px-4 py-10">
    <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-blue-600/20 blur-[120px]" />
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="glass relative w-full max-w-md rounded-3xl p-8 sm:p-10">
      <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white" data-testid="reset-back-link"><ArrowLeft size={14} /> Back to sign in</Link>
      <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-white">{title}</h1>
      <p className="mt-2 text-sm text-zinc-400">{sub}</p>
      <div className="mt-8">{children}</div>
    </motion.div>
  </div>
);

const Note = ({ ok, children, testid }) => (
  <div className={`flex gap-2.5 rounded-xl border px-4 py-3 text-sm ${ok ? "border-emerald-400/30 bg-emerald-500/10 text-emerald-200" : "border-rose-500/30 bg-rose-500/10 text-rose-200"}`} data-testid={testid}>
    {ok ? <CheckCircle size={18} weight="fill" className="shrink-0" /> : <WarningCircle size={18} weight="fill" className="shrink-0" />}<span>{children}</span>
  </div>
);

function Forgot() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ busy: false, done: "", err: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, done: "", err: "" });
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setState({ busy: false, done: r.data.message, err: "" });
    } catch (er) {
      setState({ busy: false, done: "", err: errMsg(er) });
    }
  };
  return (
    <Shell title="Forgot password?" sub="Enter the email you sign in with. We'll email you a link to set a new password.">
      {state.done ? (
        <div className="space-y-4">
          <Note ok testid="forgot-success">{state.done}</Note>
          <p className="text-xs text-zinc-500">The link works for 24 hours. Check your spam folder too. Accounts made by your school without a personal email can't use this — ask the school office to reset your login.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <label className="block"><span className="eyebrow mb-2 block">Email</span>
            <input className="field !py-3" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" data-testid="forgot-email-input" /></label>
          {state.err && <Note testid="forgot-error">{state.err}</Note>}
          <button className="btn-primary w-full !py-3" disabled={state.busy} data-testid="forgot-submit-btn"><EnvelopeSimple size={16} /> {state.busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
    </Shell>
  );
}

function Reset({ token }) {
  const [valid, setValid] = useState(null);
  const [pw, setPw] = useState({ a: "", b: "" });
  const [state, setState] = useState({ busy: false, done: false, err: "" });
  useEffect(() => {
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`).then(() => setValid(true)).catch((e) => { setValid(false); setState((s) => ({ ...s, err: errMsg(e) })); });
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return setState({ busy: false, done: false, err: "The two passwords don't match." });
    setState({ busy: true, done: false, err: "" });
    try {
      await api.post("/auth/reset-password", { token, new_password: pw.a });
      setState({ busy: false, done: true, err: "" });
    } catch (er) {
      setState({ busy: false, done: false, err: errMsg(er) });
    }
  };
  if (state.done) {
    return (
      <Shell title="Password updated" sub="You can now sign in with your new password. Other devices have been signed out.">
        <Link to="/login" className="btn-primary w-full !py-3" data-testid="reset-signin-btn">Go to sign in</Link>
      </Shell>
    );
  }
  return (
    <Shell title="Set a new password" sub="Choose a password with at least 8 characters.">
      {valid === false ? (
        <div className="space-y-4"><Note testid="reset-invalid">{state.err}</Note><Link to="/forgot-password" className="btn-ghost w-full" data-testid="reset-request-new">Request a new link</Link></div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          {["a", "b"].map((k) => (
            <label key={k} className="block"><span className="eyebrow mb-2 block">{k === "a" ? "New password" : "Confirm new password"}</span>
              <input className="field !py-3" type="password" required minLength={8} autoComplete="new-password" value={pw[k]} onChange={(e) => setPw({ ...pw, [k]: e.target.value })} data-testid={`reset-password-${k === "a" ? "input" : "confirm"}`} /></label>
          ))}
          {state.err && <Note testid="reset-error">{state.err}</Note>}
          <button className="btn-primary w-full !py-3" disabled={state.busy || valid !== true} data-testid="reset-submit-btn"><LockKey size={16} /> {state.busy ? "Saving…" : "Save new password"}</button>
        </form>
      )}
    </Shell>
  );
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  return token ? <Reset token={token} /> : <Forgot />;
}
